import { useMemo } from 'react'

import { shallowEqual, useSelector } from 'react-redux'

import { EMPTY_OBJECT } from '@constants'
import { useCampaignContext } from '@context/Campaign'
import { selectBudgetTotalCurrency } from '@store/campaigns'
import { IApplicationState } from '@store/index'
import {
  selectIdentityBrandConnectMetrics,
  selectIsFetchingPrePurchaseAnalytics,
  selectPrePurchaseAnalytics,
  selectPrePurchaseAnalyticsType,
} from '@store/prePurchaseAnalytics'
import {
  selectActiveSubmissionMediaObject,
  selectEstimatedCPECurrency,
  selectIdentityId,
  selectIsProxySubmission,
  selectSocialSubmissionType,
  selectSubmission,
  selectSubTotalCurrency,
} from '@store/submissions'
import { FINANCIAL_ANALYTICS_KEYS } from './mapping-functions'
import { transformYouTubeBrandConnectMetrics } from './transformers'
import { AnalyticsData } from './types'

export const usePrePurchaseBreakdown = (submissionId: number) => {
  const { isUnlockedCampaign } = useCampaignContext()

  // Select only raw, reference-stable values (stored refs + primitives). The
  // presentation transform below builds fresh objects, which would loop under
  // react-redux 9 if returned straight from useSelector — so it runs in useMemo.
  const raw = useSelector((state: IApplicationState) => {
    const { identity_provider, identity_followers_count, brief_id, is_yt_brand_connect } =
      selectSubmission(state, submissionId)
    const identityId = selectIdentityId(state, submissionId)
    const estCpeCurrency = selectEstimatedCPECurrency(state, submissionId)
    const campaignBudgetCurrency = selectBudgetTotalCurrency(state, brief_id)
    const activeMediaObject = selectActiveSubmissionMediaObject(state, submissionId)

    return {
      identityId,
      analyticsType: selectPrePurchaseAnalyticsType(state, submissionId),
      isFetching: selectIsFetchingPrePurchaseAnalytics(state),
      rawAnalytics: selectPrePurchaseAnalytics(state, submissionId, identityId) ?? EMPTY_OBJECT,
      brandConnectMetrics: selectIdentityBrandConnectMetrics(state, identityId),
      identityFollowersCount: identity_followers_count,
      isBrandConnectEnabled: is_yt_brand_connect,
      currency:
        selectSubTotalCurrency(state, submissionId) ?? estCpeCurrency ?? campaignBudgetCurrency,
      socialSubmissionType: selectSocialSubmissionType(state, submissionId),
      channel: identity_provider,
      mediaType: activeMediaObject.media_type,
      isProxy: selectIsProxySubmission(state, submissionId),
    }
  }, shallowEqual)

  return useMemo(() => {
    const {
      identityId,
      analyticsType,
      isFetching,
      rawAnalytics,
      brandConnectMetrics,
      identityFollowersCount,
      isBrandConnectEnabled,
      currency,
      socialSubmissionType,
      channel,
      mediaType,
      isProxy,
    } = raw

    // Transform raw API data to presentation format for Brand Connect
    const prePurchaseAnalytics = isBrandConnectEnabled
      ? transformYouTubeBrandConnectMetrics(rawAnalytics)
      : rawAnalytics

    const analytics: AnalyticsData = {
      followers: identityFollowersCount,
      ...prePurchaseAnalytics,
    }

    const isAnalyticsEmpty = Object.values(prePurchaseAnalytics).every(
      (value) => value === null || value === undefined,
    )

    const BRAND_CONNECT_HIDDEN_METRICS = [
      // Performance section (entire section hidden)
      'est_cpe',
      'est_cpv',
      'est_cpm',
      'est_emv_multiplier',
      // Overview section metrics to hide
      'avg_emv',
      'avg_impressions_pct',
      'avg_reach_pct',
    ]

    if (isBrandConnectEnabled) {
      BRAND_CONNECT_HIDDEN_METRICS.forEach((key) => {
        delete analytics[key as keyof AnalyticsData]
      })
    }

    if (isUnlockedCampaign) {
      FINANCIAL_ANALYTICS_KEYS.forEach((key) => {
        analytics[key] = undefined
      })
    }

    return {
      identityId,
      analyticsType,
      isFetching,
      analytics,
      currency,
      socialSubmissionType,
      channel,
      mediaType,
      isProxy,
      isAnalyticsEmpty,
      isYouTubeBrandConnect: isBrandConnectEnabled,
      brandConnectMetrics,
    }
  }, [raw, isUnlockedCampaign])
}
